/**
 * H5 Games Ads Service
 * Google Ad Placement API 래퍼
 * 
 * 게임용으로 설계된 광고 API:
 * - 전면 광고 (Interstitial): 레벨 사이, 게임 종료 후 등 자연스러운 타이밍
 * - 보상형 광고 (Rewarded): 사용자가 선택적으로 시청하고 보상 획득
 */

// 타입 정의
type AdBreakType = 'preroll' | 'start' | 'pause' | 'next' | 'browse' | 'reward';
type BreakStatus = 
  | 'notReady' 
  | 'timeout' 
  | 'invalid' 
  | 'error' 
  | 'noAdPreloaded' 
  | 'frequencyCapped' 
  | 'ignored' 
  | 'other' 
  | 'dismissed' 
  | 'viewed';

interface PlacementInfo {
  breakType: AdBreakType;
  breakName: string;
  breakFormat: 'interstitial' | 'reward';
  breakStatus: BreakStatus;
}

interface AdBreakConfig {
  type: AdBreakType;
  name?: string;
  beforeAd?: () => void;
  afterAd?: () => void;
  adBreakDone?: (placementInfo: PlacementInfo) => void;
  // Rewarded ads only
  beforeReward?: (showAdFn: () => void) => void;
  adDismissed?: () => void;
  adViewed?: () => void;
}

interface AdConfigOptions {
  sound?: 'on' | 'off';
  preloadAdBreaks?: 'on' | 'auto';
  onReady?: () => void;
}

// Global declarations
declare global {
  interface Window {
    adsbygoogle: unknown[];
    adBreak: (config: AdBreakConfig) => void;
    adConfig: (config: AdConfigOptions) => void;
  }
}

class H5GamesAdsService {
  private isInitialized = false;
  private isReady = false;
  private soundEnabled = true;
  private pendingCallbacks: Array<() => void> = [];

  /**
   * 서비스 초기화 확인
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * 광고 준비 상태 확인
   */
  get ready(): boolean {
    return this.isReady;
  }

  /**
   * 사운드 활성화 상태
   */
  get isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  /**
   * API 초기화 (index.html에서 스크립트 로드 후 자동 호출됨)
   */
  initialize(): void {
    if (this.isInitialized) return;

    // adsbygoogle 배열이 있는지 확인
    if (typeof window.adsbygoogle === 'undefined') {
      window.adsbygoogle = [];
    }

    // adBreak, adConfig 함수 초기화
    if (typeof window.adBreak === 'undefined') {
      window.adBreak = window.adConfig = function(o: AdBreakConfig | AdConfigOptions) {
        window.adsbygoogle.push(o);
      };
    }

    this.isInitialized = true;
    console.log('[H5GamesAds] Initialized');

    // 초기 설정
    this.configure({
      sound: 'on',
      preloadAdBreaks: 'on',
      onReady: () => {
        this.isReady = true;
        console.log('[H5GamesAds] Ready to show ads');
        // 대기 중인 콜백 실행
        this.pendingCallbacks.forEach(cb => cb());
        this.pendingCallbacks = [];
      }
    });
  }

  /**
   * 광고 설정 변경
   */
  configure(options: AdConfigOptions): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    if (options.sound) {
      this.soundEnabled = options.sound === 'on';
    }

    window.adConfig(options);
    console.log('[H5GamesAds] Config updated:', options);
  }

  /**
   * 사운드 설정 토글
   */
  setSound(enabled: boolean): void {
    this.configure({ sound: enabled ? 'on' : 'off' });
  }

  /**
   * 전면 광고 표시 (게임 종료 후, 다음 레벨 등)
   * 
   * @param type - 광고 타이밍 유형
   * @param name - 광고 배치 이름 (분석용)
   * @param callbacks - 콜백 함수들
   */
  showInterstitial(
    type: 'next' | 'pause' | 'browse' = 'next',
    name: string = 'game-interstitial',
    callbacks?: {
      onStart?: () => void;
      onComplete?: () => void;
      onError?: (status: BreakStatus) => void;
    }
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.isInitialized) {
        this.initialize();
      }

      const config: AdBreakConfig = {
        type,
        name,
        beforeAd: () => {
          console.log('[H5GamesAds] Interstitial starting');
          callbacks?.onStart?.();
        },
        afterAd: () => {
          console.log('[H5GamesAds] Interstitial ended');
          callbacks?.onComplete?.();
        },
        adBreakDone: (placementInfo) => {
          console.log('[H5GamesAds] adBreakDone:', placementInfo);
          
          if (placementInfo.breakStatus === 'viewed') {
            resolve(true);
          } else {
            // 광고가 표시되지 않은 경우
            callbacks?.onError?.(placementInfo.breakStatus);
            resolve(false);
          }
        }
      };

      window.adBreak(config);
    });
  }

  /**
   * 보상형 광고 표시
   * 사용자가 광고를 끝까지 시청하면 보상 지급
   * 
   * @param name - 광고 배치 이름
   * @param callbacks - 콜백 함수들
   */
  showRewarded(
    name: string = 'game-reward',
    callbacks?: {
      onRewardPrompt?: (showAd: () => void) => void;
      onStart?: () => void;
      onRewarded?: () => void;
      onDismissed?: () => void;
      onComplete?: () => void;
      onError?: (status: BreakStatus) => void;
    }
  ): Promise<{ rewarded: boolean; status: BreakStatus }> {
    return new Promise((resolve) => {
      if (!this.isInitialized) {
        this.initialize();
      }

      let showAdFn: (() => void) | null = null;

      const config: AdBreakConfig = {
        type: 'reward',
        name,
        beforeReward: (fn) => {
          console.log('[H5GamesAds] Reward ad available');
          showAdFn = fn;
          
          // 보상 프롬프트 콜백이 있으면 사용자에게 선택권 부여
          if (callbacks?.onRewardPrompt) {
            callbacks.onRewardPrompt(() => {
              showAdFn?.();
            });
          } else {
            // 콜백이 없으면 바로 광고 표시
            fn();
          }
        },
        beforeAd: () => {
          console.log('[H5GamesAds] Rewarded ad starting');
          callbacks?.onStart?.();
        },
        adViewed: () => {
          console.log('[H5GamesAds] Rewarded ad viewed - granting reward');
          callbacks?.onRewarded?.();
        },
        adDismissed: () => {
          console.log('[H5GamesAds] Rewarded ad dismissed - no reward');
          callbacks?.onDismissed?.();
        },
        afterAd: () => {
          console.log('[H5GamesAds] Rewarded ad ended');
          callbacks?.onComplete?.();
        },
        adBreakDone: (placementInfo) => {
          console.log('[H5GamesAds] Rewarded adBreakDone:', placementInfo);
          
          const rewarded = placementInfo.breakStatus === 'viewed';
          
          if (!rewarded && placementInfo.breakStatus !== 'dismissed') {
            callbacks?.onError?.(placementInfo.breakStatus);
          }
          
          resolve({ rewarded, status: placementInfo.breakStatus });
        }
      };

      window.adBreak(config);
    });
  }

  /**
   * 게임 시작 전 프리롤 광고
   */
  showPreroll(
    callbacks?: {
      onStart?: () => void;
      onComplete?: () => void;
    }
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.isInitialized) {
        this.initialize();
      }

      window.adBreak({
        type: 'preroll',
        name: 'game-preroll',
        beforeAd: () => {
          callbacks?.onStart?.();
        },
        afterAd: () => {
          callbacks?.onComplete?.();
        },
        adBreakDone: (info) => {
          resolve(info.breakStatus === 'viewed');
        }
      });
    });
  }

  /**
   * 준비 완료 시 콜백 실행
   */
  onReady(callback: () => void): void {
    if (this.isReady) {
      callback();
    } else {
      this.pendingCallbacks.push(callback);
    }
  }
}

// 싱글톤 인스턴스
export const h5GamesAds = new H5GamesAdsService();

// 편의 함수들
export const showInterstitialAd = h5GamesAds.showInterstitial.bind(h5GamesAds);
export const showRewardedAd = h5GamesAds.showRewarded.bind(h5GamesAds);
export const initializeAds = h5GamesAds.initialize.bind(h5GamesAds);
